/** @file Weights panel (dock component): reads the stores, resolves the mesh, commits through E8's dispatch. */
import type { CommandCall } from '@pose2d/format/types';
import { useCallback, useState } from 'react';
import { useStore } from 'zustand';
import { errorText } from '../../shell/i18n/errorText';
import { useT } from '../../shell/i18n/useT';
import { docStore } from '../../stores/docStore';
import { selectionStore } from '../../stores/selectionStore';
import { toolStore } from '../../stores/toolStore';
import { editMeshStore } from '../../tools/editMesh/editMeshStore';
import { meshTargetStore } from '../../tools/editMesh/meshTargetStore';
import { brushStore } from '../../tools/paintWeights/brushStore';
import { WeightsBody } from './WeightsBody';
import { weightsIo } from './weightsIo';
import { weightsTarget } from './weightsTarget';

/** Follows the mesh the active mesh tool resolved, else the selection's mesh at setup; one batch per button. */
export function WeightsPanel() {
  const t = useT();
  const doc = useStore(docStore, weightsIo.doc);
  const selection = useStore(selectionStore);
  const published = useStore(meshTargetStore, (s) => s.target);
  const meshTool = useStore(toolStore, (s) => s.active === 'editMesh' || s.active === 'paintWeights');
  const brush = useStore(brushStore);
  const options = useStore(editMeshStore);
  const animate = useStore(toolStore, (s) => s.mode === 'animate');
  const [failure, setFailure] = useState<string | null>(null);
  const commit = useCallback((calls: readonly CommandCall[]) => {
    setFailure(null);
    weightsIo.commit(calls).catch((thrown: unknown) => { setFailure(errorText(t, thrown)); });
  }, [t]);
  if (doc === null) return null;
  const target = weightsTarget(doc, selection, meshTool ? published : null);
  return <WeightsBody doc={doc} target={target} selection={selection} brush={brush} options={options} animate={animate} failure={failure} t={t} commit={commit} />;
}
